import React, { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Form, Button, Alert, message, Space } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import DataCard from '../components/page/DataCard';
import { RobotOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { useApp } from '../context/AppContext';
import { Role, View } from '../constants/roles';
import AdvisoryFirmBasicsFields from '../components/AdvisoryFirmBasicsFields';
import AdvisoryFirmBrandingFields from '../components/AdvisoryFirmBrandingFields';
import AdvisoryFirmPaymentLinksEditor from '../components/AdvisoryFirmPaymentLinksEditor';
import AdvisoryFirmAiDraftModal from '../components/AdvisoryFirmAiDraftModal';
import AdvisoryFirmOnboarding from './AdvisoryFirmOnboarding';
import { useAdvisoryFirmAiDraft } from '../hooks/useAdvisoryFirmAiDraft';
import { apiErrorFromResponse, userMessageForError } from '../utils/apiError';
import { formatDdMmYyyy } from "../utils/formatDdMmYyyy";


/** Parses AdvisoryFirm.founderProfileJson (see AdvisoryFirmBasicsFields.jsx's note on the
 *  assumed { summary } shape) back into the plain-text `founderSummary` form field. */
function founderSummaryFromJson(raw) {
    if (!raw) return '';
    try {
        const parsed = JSON.parse(raw);
        return typeof parsed?.summary === 'string' ? parsed.summary : '';
    } catch {
        return typeof raw === 'string' ? raw : '';
    }
}

function paymentLinksFromJson(raw) {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((p) => p && p.label && p.url) : [];
    } catch {
        return [];
    }
}

/**
 * AdvisoryFirmSettings — the single "My Firm" nav destination for a Lead Advisor.
 * Cites: .claude/design/la-onboarding-razorpay.md, Workstream A.
 *
 * Decides between three real UI states plus loading (per the "loading / empty / error are
 * three distinct renders" rule):
 *   1. loading  — GET /api/v1/me/advisory-firm in flight.
 *   2. no firm  — renders AdvisoryFirmOnboarding (the wizard) in place.
 *   3. error    — the fetch failed in a way that is NOT a legitimate "no firm yet" business
 *                 404 (e.g. the route isn't registered at all — true this wave, see below).
 *   4. ready    — renders the edit form, prefilled from the fetched firm.
 *
 * BACKEND NOT YET LIVE this wave: `/api/v1/me/advisory-firm*` is documented but not wired into
 * routes.ts and the Prisma migration hasn't run (see dispatching task). A GET here will almost
 * certainly hit state 3 (Express's default 404 has no JSON body) until that lands — this is the
 * correct, honest behaviour: it must NOT be misread as "no firm yet" and silently drop every
 * Lead Advisor into the onboarding wizard on every visit before the backend exists.
 */
const AdvisoryFirmSettings = () => {
    const { user, setCurrentView } = useApp();
    const { instance, accounts } = useMsal();
    const [form] = Form.useForm();

    const [status, setStatus] = useState('loading'); // 'loading' | 'error' | 'no-firm' | 'ready'
    const [errorMessage, setErrorMessage] = useState(null);
    const [firm, setFirm] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);
    const [saving, setSaving] = useState(false);

    const getApiToken = useCallback(async () => {
        const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account: accounts[0] });
        return tokenResponse.accessToken;
    }, [instance, accounts]);

    const { aiModal, runAiEnrichment, applyDraftField, applyAllDraftFields, closeAiModal } =
        useAdvisoryFirmAiDraft(form, getApiToken);

    const isLeadAdvisor = user?.role === Role.LEAD_ADVISOR;

    const loadFirm = useCallback(async () => {
        setStatus('loading');
        setErrorMessage(null);
        try {
            const token = await getApiToken();
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/me/advisory-firm`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const contentType = res.headers.get('content-type') || '';
            const json = contentType.includes('json') ? await res.json().catch(() => null) : null;

            if (res.ok && json?.success) {
                const row = json.data?.firm ?? json.data ?? null;
                if (!row) {
                    setStatus('no-firm');
                } else {
                    setFirm(row);
                    setLogoUrl(row.logoBlobUrl || null);
                    form.setFieldsValue({
                        firmName: row.firmName,
                        legalName: row.legalName,
                        gstin: row.gstin,
                        pan: row.pan,
                        address: row.address,
                        city: row.city,
                        state: row.state,
                        country: row.country || 'India',
                        pincode: row.pincode,
                        website: row.website,
                        linkedInUrl: row.linkedInUrl,
                        brandPrimary: row.brandPrimary,
                        brandFont: row.brandFont,
                        aboutFirm: row.aboutFirm,
                        founderSummary: founderSummaryFromJson(row.founderProfileJson),
                        paymentLinks: paymentLinksFromJson(row.paymentLinksJson),
                    });
                    setStatus('ready');
                }
                return;
            }

            // A genuine "no AdvisoryFirm row yet" business response — only trust this when the
            // backend actually returned a parsed JSON error, never a bare HTTP 404 (which today,
            // pre-wiring, means "route doesn't exist" — see the file header note).
            if (res.status === 404 && json?.error) {
                setStatus('no-firm');
                return;
            }

            throw apiErrorFromResponse(res, json);
        } catch (err) {
            console.warn('advisory firm fetch failed', err);
            setErrorMessage(userMessageForError(err, 'Could not load the firm profile.'));
            setStatus('error');
        }
    }, [getApiToken, form]);

    useEffect(() => {
        if (!isLeadAdvisor) return;
        loadFirm();
        // Deliberately NOT depending on `loadFirm` (a new function reference every render since
        // it closes over `form`) — this should run once per persona-eligibility change, not on
        // every render. NOTE: the exhaustive-deps hooks rule is not loaded by this build's
        // ESLint config (see .claude/lessons/eslint-plugin-react-hooks-breaks-cra-build.md), so
        // a real suppression comment for it would itself break the build (that lesson's own
        // finding) — this plain-English note stands in for one instead.
    }, [isLeadAdvisor]);

    const handleSaveChanges = async () => {
        try {
            await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        try {
            const values = form.getFieldsValue(true);
            const paymentLinks = (values.paymentLinks || []).filter((p) => p?.label && p?.url);
            const payload = {
                firmName: values.firmName,
                legalName: values.legalName || null,
                gstin: values.gstin || null,
                pan: values.pan || null,
                address: values.address || null,
                city: values.city || null,
                state: values.state || null,
                country: values.country || 'India',
                pincode: values.pincode || null,
                website: values.website || null,
                linkedInUrl: values.linkedInUrl || null,
                logoBlobUrl: logoUrl,
                brandPrimary: values.brandPrimary || null,
                brandFont: values.brandFont || null,
                aboutFirm: values.aboutFirm || null,
                founderProfileJson: values.founderSummary ? JSON.stringify({ summary: values.founderSummary }) : null,
                paymentLinksJson: JSON.stringify(paymentLinks),
            };
            const token = await getApiToken();
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/me/advisory-firm`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload),
            });
            const json = await res.json().catch(() => null);
            if (!res.ok || !json || !json.success) {
                throw new Error(
                    json?.error?.message ||
                    `Save failed (${res.status}). This route isn't wired on the backend yet this wave.`,
                );
            }
            message.success('Firm profile updated.');
            setFirm(json.data?.firm || json.data || firm);
        } catch (err) {
            console.warn('advisory firm update failed', err);
            message.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Persona gate ────────────────────────────────────────────────────────────────────────
    // Full ABAC enforcement is the backend's job (Commandment III/V) — this is a UX nicety only.
    if (user?.role === Role.ADMIN) {
        return (
            <PageState
                status="empty"
                message={
                    <span>
                        Admin support access to a specific Lead Advisor's firm profile isn't available yet -
                        the documented API contract (<code>GET/POST/PATCH /api/v1/me/advisory-firm</code>) is scoped
                        to the calling Lead Advisor only. Viewing another LA's firm needs a new admin-scoped
                        backend route, not yet designed. Flagged for follow-up.
                    </span>
                }
            />
        );
    }
    if (!isLeadAdvisor) {
        return <PageState status="empty" message="This page is only available to Lead Advisors." />;
    }

    // ── Loading / error / no-firm / ready — four distinct renders ──────────────────────────
    if (status === 'loading') {
        return <PageState status="loading" />;
    }

    if (status === 'error') {
        return (
            <PageShell width="narrow">
                <Alert
                    type="warning"
                    showIcon
                    message="The firm profile could not be loaded"
                    description={errorMessage}
                />
                <Button style={{ marginTop: 16 }} icon={<ReloadOutlined />} onClick={loadFirm}>
                    Retry
                </Button>
            </PageShell>
        );
    }

    if (status === 'no-firm') {
        return <AdvisoryFirmOnboarding onCreated={() => { message.success('Welcome aboard!'); setCurrentView(View.DASHBOARD); }} />;
    }

    // status === 'ready'
    return (
        <PageShell width="narrow">
            <PageHeader title="My Firm" subtitle={`Last updated ${formatDdMmYyyy(firm?.updatedAt)}`} />

            <DataCard>
                <Form form={form} layout="vertical">
                    <AdvisoryFirmBasicsFields />
                    <Button icon={<RobotOutlined />} onClick={runAiEnrichment} style={{ marginBottom: 24 }}>
                        Re-run AI auto-fetch
                    </Button>

                    <AdvisoryFirmBrandingFields getApiToken={getApiToken} logoUrl={logoUrl} onLogoUploaded={setLogoUrl} />

                    <AdvisoryFirmPaymentLinksEditor />
                </Form>
            </DataCard>

            <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveChanges}>
                    Save changes
                </Button>
            </Space>

            <AdvisoryFirmAiDraftModal
                open={aiModal.open}
                loading={aiModal.loading}
                error={aiModal.error}
                draft={aiModal.draft}
                appliedKeys={aiModal.appliedKeys}
                onApply={applyDraftField}
                onApplyAll={applyAllDraftFields}
                onClose={closeAiModal}
            />
        </PageShell>
    );
};

export default AdvisoryFirmSettings;
